import React from "react";

const readOpenItems = (storageKey: string): Set<string> => {
    try {
        const savedOpenItems = window.localStorage.getItem(storageKey);
        if (!savedOpenItems) {
            return new Set<string>();
        }

        const parsedOpenItems = JSON.parse(savedOpenItems);
        return Array.isArray(parsedOpenItems)
            ? new Set<string>(parsedOpenItems.filter((id): id is string => typeof id === "string"))
            : new Set<string>();
    } catch {
        return new Set<string>();
    }
};

/**
 * Expanded tree items kept in localStorage under `storageKey`.
 * When nothing was saved (also a saved empty list), the root item is opened once it is known. That happens only
 * once per mount, so the user can still collapse the root.
 */
export function usePersistedOpenItems(storageKey: string, rootId: string | undefined) {
    const [openItems, setOpenItems] = React.useState<Set<string>>(() => {
        const savedOpenItems = readOpenItems(storageKey);
        return savedOpenItems.size === 0 && rootId ? new Set([rootId]) : savedOpenItems;
    });
    const openRootPending = React.useRef(openItems.size === 0);

    React.useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(Array.from(openItems)));
        } catch {
            // Storage can be unavailable (private mode, blocked site data); the tree still works without it.
        }
    }, [storageKey, openItems]);

    React.useEffect(() => {
        if (!rootId || !openRootPending.current) {
            return;
        }

        openRootPending.current = false;
        setOpenItems((current) => (current.size > 0 ? current : new Set([rootId])));
    }, [rootId]);

    return [openItems, setOpenItems] as const;
}
